export type AuthRole =
  | 'viewer'
  | 'readonly'
  | 'partner'
  | 'operator'
  | 'risk_admin'
  | 'admin'
  | 'super_admin';

const DEFAULT_ROLE: AuthRole = 'viewer';

const ROLE_CAPABILITIES: Record<AuthRole, string[]> = {
  viewer: ['dashboard:read'],
  readonly: ['dashboard:read'],
  // Partner viewer — curated read-only operator-console access (stripped shell:
  // no sidebar, no active setups, scoped read-only Auto-Trader overview). Same
  // read capability as viewer; the restricted UX is driven off role === 'partner'
  // (see App.tsx → PartnerShell) and the backend write-blocks the role.
  partner: ['dashboard:read'],
  operator: ['dashboard:read', 'trade:execute', 'trade:manage', 'autotrader:toggle'],
  risk_admin: [
    'dashboard:read',
    'trade:execute',
    'trade:manage',
    'autotrader:toggle',
    'settings:write',
    'settings:reset',
    'admin:read',
    'history:clear',
    'emergency:close_all',
    'stats:clear',
  ],
  admin: ['*'],
  super_admin: ['*'],
};

export function normalizeRole(value: string | null | undefined): AuthRole {
  const role = String(value || '').trim().toLowerCase();
  if (role in ROLE_CAPABILITIES) return role as AuthRole;
  return DEFAULT_ROLE;
}

export function resolveCapabilities(role: string | null | undefined): Set<string> {
  const normalized = normalizeRole(role);
  return new Set(ROLE_CAPABILITIES[normalized]);
}

export function hasCapability(role: string | null | undefined, capability: string): boolean {
  const caps = resolveCapabilities(role);
  return caps.has('*') || caps.has(capability);
}
